/**
 * A translated page never falls over (Sentry FLY-LOCAL-BOOST-AM, 30 Oct 26: Facebook ad visitors on
 * Android crashed on the start page's phone step; Shardul saw it on his phone too).
 *
 * Page translation (Chrome Translate on Android and iPhone, Safari's, Edge's) swaps the page's bare text
 * for nodes of its own behind React's back. React then asks to put a node in front of, or take out, a
 * text node that is no longer there, the browser throws ("Failed to execute 'insertBefore' on 'Node'",
 * in Safari "The object can not be found here.") and the whole page drops to Something went wrong.
 *
 * The guard changes only the calls that would have thrown for that reason: a node to go in front of one
 * that has gone goes in at the end instead, and a node to be taken out that is no longer there is left
 * alone. Every other call runs as before. This is the known React workaround (facebook/react#11538);
 * the start page also keeps a button's words in their own span, so the spinner never needs this.
 */

type NodeProto = Pick<Node, 'insertBefore' | 'removeChild'>;

/** Installs the guard on the given prototype (the page's Node by default); returns the undo, for tests. */
export function installTranslateGuard(proto: NodeProto | undefined = typeof Node === 'function' ? Node.prototype : undefined): () => void {
  if (!proto) return () => {};
  const originalInsertBefore = proto.insertBefore;
  const originalRemoveChild = proto.removeChild;
  let warned = false;
  const warnOnce = (what: string) => {
    if (warned) return;
    warned = true;
    // A warning, not an error: Sentry takes console errors, and the page carries on fine.
    console.warn(`[translateGuard] ${what}: the page was likely translated; carried on instead of crashing.`);
  };

  proto.insertBefore = function <T extends Node>(this: Node, node: T, child: Node | null): T {
    if (child && child.parentNode !== this) {
      warnOnce('insertBefore on a node that is no longer here');
      return originalInsertBefore.call(this, node, null) as T;
    }
    return originalInsertBefore.call(this, node, child) as T;
  };
  proto.removeChild = function <T extends Node>(this: Node, child: T): T {
    if (child.parentNode !== this) {
      warnOnce('removeChild on a node that is no longer here');
      return child;
    }
    return originalRemoveChild.call(this, child) as T;
  };

  return () => {
    proto.insertBefore = originalInsertBefore;
    proto.removeChild = originalRemoveChild;
  };
}
